import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Search, AlertTriangle, CheckCircle, XCircle,
  Loader, RefreshCw, Globe, Zap, Shield,
  Eye, Smartphone, Monitor, Info, Sparkles, Key,
  TrendingUp, Target, Brain, ChevronDown, ChevronUp,
  Bot
} from 'lucide-react';
import './SiteAudit.css';

/* ── AI Services definition ──────────────────────────────── */
// keywords: multi-word phrases searched with word boundaries in VISIBLE TEXT only
const AI_SERVICES = [
  {
    id:'geo', acronym:'GEO', color:'#00d4ff',
    name:'Generative Engine Optimization',
    desc:'Optimizing content to appear in AI-generated search results (ChatGPT, Gemini, Perplexity).',
    benefit: 'Get cited inside ChatGPT, Gemini & Perplexity answers',
    keywords:['generative engine optimization','generative engine','generative search optimization','chatgpt optimization','cited in chatgpt','cited in gemini','appear in chatgpt','rank in ai','perplexity optimization','ai-generated answers'],
  },
  {
    id:'aeo', acronym:'AEO', color:'#8b5cf6',
    name:'Answer Engine Optimization',
    desc:'Getting content featured as direct answers in voice search, featured snippets & AI engines.',
    benefit: 'Appear as the direct answer for voice & zero-click searches',
    keywords:['answer engine optimization','answer engine','featured snippet','featured snippets','direct answer','zero-click search','position zero','voice search optimization','faq schema','speakable schema'],
  },
  {
    id:'aio', acronym:'AIO', color:'#10b981',
    name:'AI Optimization',
    desc:'Broad optimization of digital assets for AI-powered platforms and search tools.',
    benefit: 'Make your entire digital presence AI-ready and discoverable',
    keywords:['ai optimization','artificial intelligence optimization','ai-powered seo','optimize for ai','ai-ready content','ai content strategy','ai platform optimization','optimize for artificial intelligence'],
  },
  {
    id:'cao', acronym:'CAO', color:'#f59e0b',
    name:'Conversational AI Optimization',
    desc:'Optimizing for chatbots, voice assistants and conversational search queries.',
    benefit: 'Rank for natural language & voice assistant queries',
    keywords:['conversational ai optimization','conversational ai','conversational search','chatbot optimization','voice assistant optimization','natural language search','conversational queries','dialogue optimization'],
  },
  {
    id:'llmo', acronym:'LLMO', color:'#ec4899',
    name:'LLM Optimization',
    desc:'Making content discoverable and citable by Large Language Models like GPT-4, Claude, Gemini.',
    benefit: 'Have GPT-4, Claude & Gemini recommend your business',
    keywords:['llm optimization','large language model optimization','language model optimization','optimize for llm','llm-friendly content','large language model','llm seo','gpt-4 optimization'],
  },
  {
    id:'gaio', acronym:'GAIO', color:'#a855f7',
    name:'Generative AI Optimization',
    desc:'Optimizing content for generative AI tools that create new content from your website.',
    benefit: 'Become the source AI tools reference when generating content',
    keywords:['generative ai optimization','generative ai','optimize for generative ai','ai content generation','generative model optimization','generative ai search','ai generated content'],
  },
  {
    id:'aiso', acronym:'AISO', color:'#06b6d4',
    name:'AI Search Optimization',
    desc:'Optimizing for AI-powered search engines that use machine learning to rank results.',
    benefit: 'Rank higher in Bing AI, You.com & neural search engines',
    keywords:['ai search optimization','ai-powered search','ai search engine','machine learning seo','neural search','semantic search optimization','ai-driven search','ai search ranking'],
  },
  {
    id:'sro', acronym:'SRO', color:'#14b8a6',
    name:'Search Response Optimization',
    desc:'Optimizing how search engines display and respond with your content as answers.',
    benefit: 'Control how your site appears in AI-generated search responses',
    keywords:['search response optimization','search response','search result optimization','search snippet optimization','rich results optimization','serp snippet','knowledge panel optimization','search answer optimization'],
  },
];

/* ── score helpers ───────────────────────────────────────── */
const scoreColor = s => s >= 80 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444';
const scoreLabel = s => s >= 80 ? 'Good' : s >= 50 ? 'Needs Work' : 'Poor';

/* ── CORS proxy fetch ────────────────────────────────────── */
const PROXIES = [
  u => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`,
  u => `https://corsproxy.io/?${encodeURIComponent(u)}`,
];

async function fetchHTML(url) {
  for (const proxy of PROXIES) {
    try {
      const res = await fetch(proxy(url), { signal: AbortSignal.timeout(18000) });
      if (res.ok) { const t = await res.text(); if (t.trim().length > 100) return t; }
    } catch { /* try next */ }
  }
  throw new Error('Could not reach the website. Make sure it is publicly accessible.');
}

/* ── Gemini API call ─────────────────────────────────────── */
async function callGemini(apiKey, prompt) {
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
      signal: AbortSignal.timeout(30000),
    }
  );
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err?.error?.message || 'Gemini API error. Check your API key.');
  }
  const data = await res.json();
  const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
  const json = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  return JSON.parse(json);
}

/* ── HTML analyzer ───────────────────────────────────────── */
function analyzeHTML(html, targetUrl) {
  const doc        = new DOMParser().parseFromString(html, 'text/html');
  const title      = doc.querySelector('title')?.textContent?.trim() || '';
  const metaDesc   = doc.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() || '';
  const h1s        = [...doc.querySelectorAll('h1')];
  const h2s        = [...doc.querySelectorAll('h2')];
  const canonical  = doc.querySelector('link[rel="canonical"]');
  const viewport   = doc.querySelector('meta[name="viewport"]');
  const htmlLang   = doc.documentElement.getAttribute('lang') || '';
  const ogTitle    = doc.querySelector('meta[property="og:title"]');
  const ogDesc     = doc.querySelector('meta[property="og:description"]');
  const ogImage    = doc.querySelector('meta[property="og:image"]');
  const robots     = doc.querySelector('meta[name="robots"]');
  const favicon    = doc.querySelector('link[rel="icon"],link[rel="shortcut icon"],link[rel="apple-touch-icon"]');
  const jsonLd     = doc.querySelectorAll('script[type="application/ld+json"]');
  const allImgs    = [...doc.querySelectorAll('img')];
  const allLinks   = [...doc.querySelectorAll('a')];
  const allScripts = [...doc.querySelectorAll('script[src]')];
  const allInputs  = [...doc.querySelectorAll('input,textarea,select')];
  const inlineEv   = (html.match(/\s(onclick|onload|onmouseover|onerror)=/gi) || []).length;
  const preconnect = doc.querySelectorAll('link[rel="preconnect"],link[rel="dns-prefetch"]');
  const lazyImgs   = allImgs.filter(i => i.getAttribute('loading') === 'lazy').length;
  const deferred   = allScripts.filter(s => s.hasAttribute('defer') || s.hasAttribute('async')).length;
  const imgsNoAlt  = allImgs.filter(i => !i.getAttribute('alt') && i.getAttribute('alt') !== '');
  const badLinks   = allLinks.filter(a => ['click here','here','read more','more','link'].includes((a.textContent||'').trim().toLowerCase()));
  const extLinks   = allLinks.filter(a => { try { return new URL(a.getAttribute('href')||'',targetUrl).origin !== new URL(targetUrl).origin; } catch { return false; } });
  const unsafeExt  = extLinks.filter(a => a.getAttribute('target')==='_blank' && !(a.getAttribute('rel')||'').toLowerCase().includes('noopener'));
  const noLabel    = allInputs.filter(inp => { const id=inp.getAttribute('id'); return !((id&&doc.querySelector(`label[for="${id}"]`))||inp.getAttribute('aria-label')||inp.getAttribute('aria-labelledby')); });
  const blocking   = [...doc.querySelectorAll('head script[src]')].filter(s=>!s.hasAttribute('defer')&&!s.hasAttribute('async'));
  const hasMain    = doc.querySelector('main,[role="main"]');
  const isHttps    = targetUrl.startsWith('https://');
  const hasDoctype = html.trimStart().toLowerCase().startsWith('<!doctype');
  const fullBodyText = (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
  const bodyText   = fullBodyText.slice(0, 4000);

  const seo = [
    { id:'title',    title:'Has <title> tag',                    passed:!!title,                          info:title?`"${title.slice(0,65)}${title.length>65?'…':''}"`:'',        detail:'Add a descriptive <title> tag inside <head>.' },
    { id:'titleLen', title:'Title length ideal (10–65 chars)',   passed:title.length>=10&&title.length<=65, info:title?`${title.length} chars`:'',                                detail:'Keep the page title between 10 and 65 characters.' },
    { id:'metaD',    title:'Has meta description',               passed:!!metaDesc,                        info:metaDesc?`${metaDesc.length} chars`:'',                           detail:'Add <meta name="description" content="…"> for snippets.' },
    { id:'metaDL',   title:'Meta description length (50–165)',   passed:metaDesc.length>=50&&metaDesc.length<=165, info:metaDesc?`${metaDesc.length} chars`:'',                  detail:'Keep meta description between 50–165 characters.' },
    { id:'h1',       title:'Page has an H1 heading',             passed:h1s.length>0,                      info:`${h1s.length} H1 found`,                                        detail:'Every page needs exactly one H1 heading.' },
    { id:'oneH1',    title:'Only one H1 on page',                passed:h1s.length===1,                    info:`${h1s.length} H1(s)`,                                           detail:'Multiple H1 tags confuse search engines. Use only one.' },
    { id:'h2',       title:'Uses H2 subheadings',                passed:h2s.length>0,                      info:`${h2s.length} H2(s)`,                                           detail:'Use H2 tags to structure your content hierarchy.' },
    { id:'canonical',title:'Has canonical URL',                  passed:!!canonical,                       info:canonical?.getAttribute('href')||'',                              detail:'Add <link rel="canonical" href="…"> to prevent duplicate content.' },
    { id:'viewport', title:'Has viewport meta tag',              passed:!!viewport,                        info:viewport?.getAttribute('content')||'',                            detail:'Add <meta name="viewport" content="width=device-width, initial-scale=1">.' },
    { id:'og',       title:'Has Open Graph tags',                passed:!!ogTitle&&!!ogDesc,               info:ogTitle?'og:title & og:description found':'',                     detail:'Add og:title and og:description meta tags.' },
    { id:'ogImg',    title:'Has og:image for social sharing',    passed:!!ogImage,                         info:ogImage?.getAttribute('content')||'',                             detail:'Add <meta property="og:image"> for rich social previews.' },
    { id:'imgAlt',   title:'All images have alt text',           passed:imgsNoAlt.length===0,              info:`${imgsNoAlt.length} missing`,                                    detail:'Add descriptive alt attributes to all images.' },
    { id:'linkTxt',  title:'No generic link text',               passed:badLinks.length===0,               info:badLinks.length?`${badLinks.length} generic link(s)`:'',          detail:'Replace "click here" with descriptive anchor text.' },
    { id:'robots',   title:'Robots meta tag present',            passed:!!robots,                          info:robots?.getAttribute('content')||'',                              detail:'Add <meta name="robots" content="index,follow">.' },
    { id:'jsonLd',   title:'Has JSON-LD structured data',        passed:jsonLd.length>0,                   info:`${jsonLd.length} schema block(s)`,                               detail:'Add JSON-LD schema so AI engines can understand your content.' },
  ];

  const perf = [
    { id:'https',    title:'Site uses HTTPS',                    passed:isHttps,                           info:isHttps?'Secure':'HTTP – not secure',                             detail:'Migrate to HTTPS for security and better rankings.' },
    { id:'doctype',  title:'Has DOCTYPE declaration',            passed:hasDoctype,                        info:hasDoctype?'<!DOCTYPE html> found':'',                            detail:'Always start with <!DOCTYPE html>.' },
    { id:'htmlSize', title:'HTML size under 150 KB',             passed:html.length<150000,                info:`${Math.round(html.length/1024)} KB`,                             detail:'Large HTML slows initial load. Minimize server output.' },
    { id:'block',    title:'No render-blocking scripts in head', passed:blocking.length===0,               info:`${blocking.length} blocking script(s)`,                          detail:'Add defer or async to scripts in <head>.' },
    { id:'defer',    title:'Scripts use defer or async',         passed:allScripts.length===0||deferred===allScripts.length, info:`${deferred}/${allScripts.length} deferred`,   detail:'Add defer/async attribute to all external scripts.' },
    { id:'lazyLoad', title:'Images use lazy loading',            passed:allImgs.length===0||lazyImgs>0,    info:`${lazyImgs}/${allImgs.length} lazy-loaded`,                      detail:'Add loading="lazy" to images below the fold.' },
    { id:'preconn',  title:'Uses preconnect / dns-prefetch',     passed:preconnect.length>0,               info:`${preconnect.length} hint(s)`,                                   detail:'Add <link rel="preconnect"> for third-party origins.' },
    { id:'imgCnt',   title:'Reasonable image count (< 40)',      passed:allImgs.length<40,                 info:`${allImgs.length} images`,                                       detail:'Too many images slow page load. Use lazy loading.' },
    { id:'inlineEv', title:'No excessive inline event handlers', passed:inlineEv<5,                        info:`${inlineEv} inline handler(s)`,                                  detail:'Move event handlers to external JS files.' },
  ];

  const a11y = [
    { id:'lang',     title:'HTML has lang attribute',            passed:!!htmlLang,                        info:htmlLang||'',                                                     detail:'Add lang="en" (or your language) to the <html> tag.' },
    { id:'altA11y',  title:'All images have alt text',           passed:imgsNoAlt.length===0,              info:`${imgsNoAlt.length} missing`,                                    detail:'Screen readers rely on alt text for images.' },
    { id:'labels',   title:'Form inputs have labels',            passed:noLabel.length===0,                info:`${noLabel.length} unlabelled input(s)`,                          detail:'Associate every form field with a <label> element.' },
    { id:'main',     title:'Page has a <main> landmark',         passed:!!hasMain,                         info:hasMain?'<main> found':'',                                        detail:'Add <main> or role="main" for screen reader navigation.' },
    { id:'descLink', title:'Links have descriptive text',        passed:badLinks.length===0,               info:badLinks.length?`${badLinks.length} non-descriptive`:'',          detail:'Replace generic link text for screen reader users.' },
    { id:'noopener', title:'External links use rel="noopener"',  passed:unsafeExt.length===0,              info:`${unsafeExt.length} unsafe external link(s)`,                    detail:'Add rel="noopener noreferrer" to _blank external links.' },
    { id:'favA11y',  title:'Site has a favicon',                 passed:!!favicon,                         info:favicon?.getAttribute('href')||'',                                detail:'Add a favicon for branding and tab identification.' },
    { id:'ogA11y',   title:'Has og:title for sharing',           passed:!!ogTitle,                         info:ogTitle?.getAttribute('content')?.slice(0,50)||'',                detail:'og:title makes social sharing accessible and descriptive.' },
  ];

  const bp = [
    { id:'https2',   title:'Uses HTTPS',                         passed:isHttps,                           info:isHttps?'HTTPS':'HTTP',                                           detail:'HTTPS is required for security and modern browser features.' },
    { id:'view2',    title:'Has viewport meta (mobile-ready)',    passed:!!viewport,                        info:viewport?.getAttribute('content')||'',                            detail:'Missing viewport makes site broken on mobile.' },
    { id:'fav2',     title:'Favicon defined',                    passed:!!favicon,                         info:favicon?.getAttribute('href')||'',                                detail:'Add a favicon in your <head>.' },
    { id:'jld2',     title:'Uses JSON-LD structured data',       passed:jsonLd.length>0,                   info:`${jsonLd.length} schema(s)`,                                     detail:'JSON-LD helps AI engines and Google understand your content.' },
    { id:'can2',     title:'Canonical URL defined',              passed:!!canonical,                       info:canonical?.getAttribute('href')||'',                              detail:'Prevents duplicate content SEO issues.' },
    { id:'ev2',      title:'Minimal inline event handlers',      passed:inlineEv<5,                        info:`${inlineEv} found`,                                              detail:'Inline events create maintenance and security issues.' },
    { id:'ext2',     title:'External _blank links are safe',     passed:unsafeExt.length===0,              info:`${unsafeExt.length} unsafe`,                                     detail:'Add rel="noopener noreferrer" to target="_blank" links.' },
    { id:'doc2',     title:'DOCTYPE declared',                   passed:hasDoctype,                        info:hasDoctype?'Present':'Missing',                                   detail:'Missing DOCTYPE causes browser quirks mode.' },
  ];

  const calc = arr => Math.round(arr.filter(c=>c.passed).length / arr.length * 100);

  /* ── AI Services Coverage ──────────────────────────────────
     Search ONLY in visible text (title + meta + headings + body).
     Raw HTML is excluded to avoid false positives from JS bundles,
     CSS class names, and HTML attributes.
     Word-boundary regex prevents partial matches (e.g. "geo" ≠ "geography").
  ──────────────────────────────────────────────────────────── */
  const headingsText = [...doc.querySelectorAll('h1,h2,h3,h4,h5,h6')]
    .map(h => h.textContent).join(' ');
  const visibleText  = [title, metaDesc, headingsText, fullBodyText].join(' ').toLowerCase();

  // Escape regex special chars in keyword, then wrap with word boundaries
  const kwRegex = kw => new RegExp(
    `\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '[\\s\\-]+')}\\b`,
    'i'
  );

  const servicesCoverage = AI_SERVICES.map(svc => {
    const found = svc.keywords.filter(kw => kwRegex(kw).test(visibleText));
    const level = found.length >= 3 ? 'covered'
                : found.length === 2 ? 'partial'
                : found.length === 1 ? 'mentioned'
                : 'missing';
    const score = found.length >= 3 ? 100 : found.length === 2 ? 66 : found.length === 1 ? 33 : 0;
    return { ...svc, found, level, score };
  });
  const coverageScore = Math.round(servicesCoverage.reduce((s, c) => s + c.score, 0) / servicesCoverage.length);

  return {
    seo, seoScore: calc(seo),
    perf, perfScore: calc(perf),
    a11y, a11yScore: calc(a11y),
    bp, bpScore: calc(bp),
    servicesCoverage, coverageScore,
    meta: { title, metaDesc, h1Count: h1s.length, imgCount: allImgs.length, linkCount: allLinks.length, scriptCount: allScripts.length, htmlSize: html.length, isHttps, hasJsonLd: jsonLd.length > 0 },
    bodyText,
  };
}

/* ── sub-components ──────────────────────────────────────── */
function ScoreCircle({ score, label, icon }) {
  const color = scoreColor(score);
  const r = 44, circ = 2 * Math.PI * r, dash = (score / 100) * circ;
  return (
    <div className="score-circle-wrap">
      <div className="score-circle-svg-wrap">
        <svg width="110" height="110" viewBox="0 0 110 110">
          <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="8" />
          <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={circ / 4}
            strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
        </svg>
        <div className="score-circle-inner"><span className="score-num" style={{ color }}>{score}</span></div>
      </div>
      <div className="score-icon">{icon}</div>
      <div className="score-label">{label}</div>
      <div className="score-grade" style={{ color }}>{scoreLabel(score)}</div>
    </div>
  );
}

function AuditItem({ title, detail, passed, info }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`audit-item ${passed ? 'passed' : 'failed'}`} onClick={() => detail && setOpen(o => !o)}>
      <div className="audit-status">
        {passed ? <CheckCircle size={16} color="#10b981" /> : <XCircle size={16} color="#ef4444" />}
      </div>
      <div className="audit-content">
        <div className="audit-title">
          {title}
          {detail && !passed && <Info size={11} style={{ opacity: 0.35, verticalAlign: 'middle', marginLeft: 4 }} />}
        </div>
        {open && detail && <div className="audit-desc">{detail}</div>}
        {info && <div className="audit-value">{info}</div>}
      </div>
    </div>
  );
}

/* ── AI insights renderer ────────────────────────────────── */
function AIInsights({ ai, url, overall }) {
  const [expanded, setExpanded] = useState({});
  const toggle = k => setExpanded(p => ({ ...p, [k]: !p[k] }));

  return (
    <div className="ai-insights-section">
      <div className="ai-insights-header">
        <div className="ai-insights-title">
          <Sparkles size={20} color="#8b5cf6" />
          <span>Gemini AI Analysis</span>
          <span className="ai-powered-badge">Powered by Gemini 2.0 Flash</span>
        </div>
      </div>

      {/* AI Scores row */}
      <div className="ai-scores-row">
        <div className="ai-score-card" style={{ borderColor: scoreColor(ai.aiScore) }}>
          <Brain size={22} color="#8b5cf6" />
          <div className="ai-score-num" style={{ color: scoreColor(ai.aiScore) }}>{ai.aiScore}</div>
          <div className="ai-score-lbl">AI Readability</div>
          <div className="ai-score-sub">How well AI can understand your content</div>
        </div>
        <div className="ai-score-card" style={{ borderColor: scoreColor(ai.aeoScore) }}>
          <Target size={22} color="#00d4ff" />
          <div className="ai-score-num" style={{ color: scoreColor(ai.aeoScore) }}>{ai.aeoScore}</div>
          <div className="ai-score-lbl">AEO Score</div>
          <div className="ai-score-sub">Likelihood of being cited in ChatGPT / Gemini</div>
        </div>
        <div className="ai-score-card" style={{ borderColor: scoreColor(overall) }}>
          <TrendingUp size={22} color="#10b981" />
          <div className="ai-score-num" style={{ color: scoreColor(overall) }}>{overall}</div>
          <div className="ai-score-lbl">Overall Score</div>
          <div className="ai-score-sub">Combined technical + AI audit score</div>
        </div>
      </div>

      {/* Summary */}
      {ai.summary && (
        <div className="ai-summary-box">
          <strong>Site Overview:</strong> {ai.summary}
        </div>
      )}

      {/* Strengths */}
      {ai.strengths?.length > 0 && (
        <div className="ai-block">
          <button className="ai-block-header" onClick={() => toggle('strengths')}>
            <span><CheckCircle size={15} color="#10b981" /> Strengths ({ai.strengths.length})</span>
            {expanded.strengths ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          {(expanded.strengths !== false) && (
            <ul className="ai-list green">
              {ai.strengths.map((s, i) => <li key={i}><CheckCircle size={12} />{s}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* AEO Recommendations */}
      {ai.aeoRecommendations?.length > 0 && (
        <div className="ai-block">
          <button className="ai-block-header" onClick={() => toggle('aeo')}>
            <span><Target size={15} color="#00d4ff" /> AEO Recommendations (Get cited in ChatGPT / Gemini)</span>
            {expanded.aeo === false ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
          </button>
          {expanded.aeo !== false && (
            <div className="ai-rec-list">
              {ai.aeoRecommendations.map((r, i) => (
                <div key={i} className="ai-rec-item">
                  <div className="ai-rec-num">{i + 1}</div>
                  <div>
                    <div className="ai-rec-title">{r.title}</div>
                    <div className="ai-rec-desc">{r.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SEO Quick Wins */}
      {ai.seoQuickWins?.length > 0 && (
        <div className="ai-block">
          <button className="ai-block-header" onClick={() => toggle('seo')}>
            <span><TrendingUp size={15} color="#f59e0b" /> SEO Quick Wins</span>
            {expanded.seo === false ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
          </button>
          {expanded.seo !== false && (
            <div className="ai-rec-list">
              {ai.seoQuickWins.map((r, i) => (
                <div key={i} className="ai-rec-item yellow">
                  <div className="ai-rec-num yellow">{i + 1}</div>
                  <div>
                    <div className="ai-rec-title">{r.title}</div>
                    <div className="ai-rec-desc">{r.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content Gaps */}
      {ai.contentGaps?.length > 0 && (
        <div className="ai-block">
          <button className="ai-block-header" onClick={() => toggle('gaps')}>
            <span><AlertTriangle size={15} color="#ef4444" /> Content Gaps</span>
            {expanded.gaps === false ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
          </button>
          {expanded.gaps !== false && (
            <ul className="ai-list red">
              {ai.contentGaps.map((g, i) => <li key={i}><XCircle size={12} />{g}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Keyword Opportunities */}
      {ai.keywordOpportunities?.length > 0 && (
        <div className="ai-block">
          <button className="ai-block-header" onClick={() => toggle('kw')}>
            <span><Search size={15} color="#8b5cf6" /> Keyword Opportunities</span>
            {expanded.kw === false ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
          </button>
          {expanded.kw !== false && (
            <div className="keyword-tags">
              {ai.keywordOpportunities.map((k, i) => (
                <span key={i} className="kw-tag">{k}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── AI Services Tab ─────────────────────────────────────── */
function ServicesCoverageTab({ siteUrl }) {
  const waMsg = encodeURIComponent(
    `Hi Rajesh! I audited ${siteUrl} using your free tool. I want expert help to optimize my site for AI search engines (GEO, AEO, LLMO, AISO and more). Can we discuss?`
  );

  return (
    <div className="services-coverage-tab">

      {/* Top alert banner */}
      <div className="ai-services-alert">
        <AlertTriangle size={20} color="#f59e0b" />
        <div>
          <strong>Your site needs AI Optimization</strong>
          <p>Most websites are invisible to AI engines like ChatGPT, Gemini & Perplexity. The 8 services below are what your site needs to get cited and discovered in the new era of AI search.</p>
        </div>
      </div>

      {/* Service cards — one per service */}
      <div className="service-cards-grid">
        {AI_SERVICES.map(svc => (
          <div key={svc.id} className="service-card service-card-promo"
            style={{ borderColor: `${svc.color}30`, background: `${svc.color}08` }}>
            <div className="service-card-top">
              <div className="service-acronym-badge"
                style={{ color: svc.color, borderColor: `${svc.color}40`, background: `${svc.color}12` }}>
                {svc.acronym}
              </div>
              <span className="service-needs-fix-badge">
                <AlertTriangle size={11} /> Needs Setup
              </span>
            </div>
            <div className="service-card-name">{svc.name}</div>
            <div className="service-card-desc">{svc.desc}</div>
            <div className="service-card-benefit">
              <CheckCircle size={12} color={svc.color} />
              <span style={{ color: svc.color }}>{svc.benefit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Expert support CTA */}
      <div className="ai-services-cta-box">
        <div className="ai-services-cta-left">
          <Bot size={36} color="#8b5cf6" />
          <div>
            <h3>Get Expert AI Optimization Support</h3>
            <p>
              Rajesh R is Kerala's #1 AI Platform Optimization specialist. He will audit your site
              in depth and implement all 8 AI optimization strategies — so your business gets
              discovered and cited in ChatGPT, Gemini, Perplexity & every major AI search engine.
            </p>
            <ul className="ai-cta-includes">
              <li><CheckCircle size={13} color="#10b981" /> Full GEO + AEO + LLMO strategy</li>
              <li><CheckCircle size={13} color="#10b981" /> Structured data & schema implementation</li>
              <li><CheckCircle size={13} color="#10b981" /> AI-friendly content rewriting</li>
              <li><CheckCircle size={13} color="#10b981" /> Monthly ranking reports in AI engines</li>
            </ul>
          </div>
        </div>
        <div className="ai-services-cta-right">
          <a
            href={`https://wa.me/917907038984?text=${waMsg}`}
            target="_blank" rel="noreferrer noopener"
            className="ai-cta-whatsapp-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp Rajesh Now
          </a>
          <a href="/contact" className="ai-cta-contact-btn">
            Book Free Consultation
          </a>
          <p className="ai-cta-note">Response within 2 hours · Kerala & Global clients</p>
        </div>
      </div>

    </div>
  );
}

/* ── main page ───────────────────────────────────────────── */
export default function SiteAudit() {
  const [url,        setUrl]       = useState('');
  const [geminiKey,  setGeminiKey] = useState('');
  const [showKey,    setShowKey]   = useState(false);
  const [loading,    setLoading]   = useState(false);
  const [aiLoading,  setAiLoading] = useState(false);
  const [report,     setReport]    = useState(null);
  const [aiReport,   setAiReport]  = useState(null);
  const [aiError,    setAiError]   = useState('');
  const [error,      setError]     = useState('');
  const [strategy,   setStrategy]  = useState('mobile');
  const [tab,        setTab]       = useState('seo');

  useEffect(() => {
    const saved = localStorage.getItem('gemini_api_key');
    if (saved) setGeminiKey(saved);
  }, []);

  const saveKey = (val) => {
    setGeminiKey(val);
    if (val.trim()) localStorage.setItem('gemini_api_key', val.trim());
    else localStorage.removeItem('gemini_api_key');
  };

  const normalize = raw => {
    let u = raw.trim();
    if (!u.startsWith('http://') && !u.startsWith('https://')) u = 'https://' + u;
    return u;
  };

  const runAudit = async () => {
    const target = normalize(url);
    setError(''); setReport(null); setAiReport(null); setAiError(''); setLoading(true);
    try {
      const html   = await fetchHTML(target);
      const result = analyzeHTML(html, target);
      const rep    = { ...result, url: target, analyzedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }), strategy };
      setReport(rep);

      if (geminiKey.trim()) {
        setAiLoading(true);
        try {
          const failedChecks = [
            ...result.seo.filter(c=>!c.passed).map(c=>c.title),
            ...result.perf.filter(c=>!c.passed).map(c=>c.title),
            ...result.a11y.filter(c=>!c.passed).map(c=>c.title),
            ...result.bp.filter(c=>!c.passed).map(c=>c.title),
          ].slice(0, 15).join(', ');

          const prompt = `You are an expert in SEO, AEO (Answer Engine Optimization), and AI search optimization.

Analyze this website and provide a comprehensive audit.

URL: ${target}
Title: ${result.meta.title}
Meta Description: ${result.meta.metaDesc}
H1 Count: ${result.meta.h1Count}
Body Content (first 4000 chars): ${result.bodyText}

Technical Scores: SEO ${result.seoScore}/100, Performance ${result.perfScore}/100, Accessibility ${result.a11yScore}/100, Best Practices ${result.bpScore}/100
Failed Checks: ${failedChecks}

Respond ONLY with valid JSON (no markdown, no code fences), exactly this structure:
{
  "aiScore": <number 0-100, how well AI engines can read and understand this content>,
  "aeoScore": <number 0-100, likelihood of being cited in ChatGPT/Gemini/Perplexity answers>,
  "summary": "<2-3 sentence objective summary of what this site does and its current state>",
  "strengths": ["<strength 1>", "<strength 2>", "<strength 3>"],
  "aeoRecommendations": [
    {"title": "<action title>", "description": "<specific how-to recommendation to get cited in AI engines>"},
    {"title": "<action title>", "description": "<specific how-to recommendation>"},
    {"title": "<action title>", "description": "<specific how-to recommendation>"}
  ],
  "seoQuickWins": [
    {"title": "<quick win>", "description": "<how to implement this in 1-2 sentences>"},
    {"title": "<quick win>", "description": "<how to implement>"}
  ],
  "contentGaps": ["<missing topic or content area>", "<missing topic>", "<missing topic>"],
  "keywordOpportunities": ["keyword1", "keyword2", "keyword3", "keyword4", "keyword5"]
}`;

          const ai = await callGemini(geminiKey.trim(), prompt);
          setAiReport(ai);
        } catch (ae) {
          setAiError(ae.message);
        } finally {
          setAiLoading(false);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const overall = report
    ? Math.round((report.seoScore + report.perfScore + report.a11yScore + report.bpScore) / 4)
    : null;

  const tabData = report ? {
    seo:      { checks: report.seo,  score: report.seoScore,      label: 'SEO',           icon: <Search size={14} /> },
    perf:     { checks: report.perf, score: report.perfScore,     label: 'Performance',    icon: <Zap size={14} /> },
    a11y:     { checks: report.a11y, score: report.a11yScore,     label: 'Accessibility',  icon: <Eye size={14} /> },
    bp:       { checks: report.bp,   score: report.bpScore,       label: 'Best Practices', icon: <Shield size={14} /> },
    services: { checks: null,        score: report.coverageScore, label: 'AI Services',    icon: <Bot size={14} /> },
  } : null;

  return (
    <>
      <Helmet>
        <title>Free Website Audit Tool – AI & SEO Analysis | Rajesh R</title>
        <meta name="description" content="Free website audit powered by Gemini AI. Analyze SEO, AEO, performance, accessibility. Get instant AI recommendations. No account required." />
      </Helmet>

      <div className="site-audit-page">

        {/* HERO */}
        <section className="audit-hero">
          <div className="container">
            <div className="audit-hero-badge"><Sparkles size={13} /><span>Free — Powered by Gemini AI</span></div>
            <h1 className="audit-hero-title">Free Website <span className="gradient-text">Audit Tool</span></h1>
            <p className="audit-hero-sub">
              Analyze your website's SEO, Performance, Accessibility & Best Practices.
              Add your free Gemini API key for AI-powered AEO insights and content recommendations.
            </p>

            <div className="audit-form-card">
              {/* Strategy toggle */}
              <div className="audit-strategy-toggle">
                <button className={`strategy-btn ${strategy==='mobile'?'active':''}`} onClick={()=>setStrategy('mobile')}><Smartphone size={14}/> Mobile</button>
                <button className={`strategy-btn ${strategy==='desktop'?'active':''}`} onClick={()=>setStrategy('desktop')}><Monitor size={14}/> Desktop</button>
              </div>

              {/* URL input */}
              <div className="audit-input-row">
                <div className="audit-input-wrap">
                  <Globe size={17} className="audit-input-icon" />
                  <input
                    type="url" className="audit-input" placeholder="yourwebsite.com"
                    value={url} onChange={e=>setUrl(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&!loading&&url.trim()&&runAudit()}
                  />
                </div>
                <button className="audit-btn" onClick={runAudit} disabled={loading||!url.trim()}>
                  {loading ? <Loader size={17} className="spin" /> : <Search size={17} />}
                  {loading ? 'Analyzing…' : 'Analyze Site'}
                </button>
              </div>

              {/* Gemini API key */}
              <div className="gemini-key-row">
                <button className="gemini-key-toggle" onClick={()=>setShowKey(v=>!v)}>
                  <Key size={13} />
                  {geminiKey ? '✓ Gemini API key saved' : 'Add Gemini API key for AI insights (optional)'}
                  {showKey ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                </button>
                {showKey && (
                  <div className="gemini-key-panel">
                    <input
                      type="password" className="gemini-key-input"
                      placeholder="AIzaSy…  (free from aistudio.google.com)"
                      value={geminiKey}
                      onChange={e=>saveKey(e.target.value)}
                    />
                    <p className="gemini-key-hint">
                      Get a free key at <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer noopener">aistudio.google.com/apikey</a>. Key is saved locally in your browser only.
                    </p>
                  </div>
                )}
              </div>

              {error && <div className="audit-error"><AlertTriangle size={15}/> {error}</div>}
            </div>
          </div>
        </section>

        {/* LOADING */}
        {loading && (
          <section className="audit-loading">
            <div className="container">
              <div className="loading-card">
                <div className="loading-spinner"><Loader size={38} className="spin" /></div>
                <h3>Analyzing your website…</h3>
                <p>Running 30+ checks on SEO, Performance, Accessibility & Best Practices{geminiKey?' + Gemini AI analysis':''}</p>
                <div className="loading-checks">
                  {['Fetching website HTML','Scanning meta tags & headings','Checking SEO signals','Analyzing accessibility','Reviewing best practices', geminiKey?'Running Gemini AI analysis…':'Calculating scores'].map((t,i)=>(
                    <div key={i} className="loading-check" style={{animationDelay:`${i*0.35}s`}}>
                      <CheckCircle size={13}/> {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* REPORT */}
        {report && tabData && (
          <section className="audit-report">
            <div className="container">

              {/* Header */}
              <div className="report-header">
                <div className="report-meta">
                  <div className="report-url"><Globe size={15}/><a href={report.url} target="_blank" rel="noreferrer noopener">{report.url}</a></div>
                  <div className="report-info">Analyzed {report.analyzedAt} · {report.strategy==='mobile'?'📱 Mobile':'🖥️ Desktop'} · 30+ checks</div>
                </div>
                <div className="report-actions">
                  <button className="report-action-btn secondary" onClick={runAudit}><RefreshCw size={14}/> Re-analyze</button>
                </div>
              </div>

              {/* Overall banner */}
              <div className="overall-score-banner" style={{borderColor:scoreColor(overall)}}>
                <div className="overall-score-left">
                  <div className="overall-score-num" style={{color:scoreColor(overall)}}>{overall}</div>
                  <div className="overall-score-label">
                    <strong>Overall Score</strong>
                    <span style={{color:scoreColor(overall)}}>{scoreLabel(overall)}</span>
                  </div>
                </div>
                <div className="overall-score-right">
                  {overall>=80?'Great job! Your site is performing well across all areas.'
                    :overall>=50?'Your site needs improvements. Check the issues below.'
                    :'Your site has critical issues that need immediate attention.'}
                </div>
              </div>

              {/* Score circles */}
              <div className="scores-grid">
                <ScoreCircle score={report.seoScore}  label="SEO"            icon={<Search size={17}/>}/>
                <ScoreCircle score={report.perfScore} label="Performance"    icon={<Zap size={17}/>}/>
                <ScoreCircle score={report.a11yScore} label="Accessibility"  icon={<Eye size={17}/>}/>
                <ScoreCircle score={report.bpScore}   label="Best Practices" icon={<Shield size={17}/>}/>
              </div>

              {/* Quick stats */}
              <div className="quick-stats">
                {[
                  {label:'Page Size',  val:`${Math.round(report.meta.htmlSize/1024)} KB`},
                  {label:'Images',     val:report.meta.imgCount},
                  {label:'Links',      val:report.meta.linkCount},
                  {label:'Scripts',    val:report.meta.scriptCount},
                  {label:'H1 Tags',    val:report.meta.h1Count},
                  {label:'HTTPS',      val:report.meta.isHttps?'✓ Yes':'✗ No'},
                  {label:'Schema',     val:report.meta.hasJsonLd?'✓ Found':'✗ None'},
                  {label:'Checks Run', val:'30+'},
                ].map(s=>(
                  <div key={s.label} className="quick-stat">
                    <div className="quick-stat-val">{s.val}</div>
                    <div className="quick-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* AI insights (if available) */}
              {aiLoading && (
                <div className="ai-loading-bar">
                  <Loader size={16} className="spin" color="#8b5cf6"/>
                  <span>Gemini AI is analyzing your content…</span>
                </div>
              )}
              {aiError && (
                <div className="audit-error" style={{marginBottom:24}}>
                  <AlertTriangle size={15}/> Gemini AI error: {aiError}
                </div>
              )}
              {aiReport && <AIInsights ai={aiReport} url={report.url} overall={overall}/>}

              {/* Tabs */}
              <div className="audit-tabs">
                {Object.entries(tabData).map(([key,d])=>(
                  <button key={key} className={`audit-tab ${tab===key?'active':''}`} onClick={()=>setTab(key)}
                    style={tab===key?{borderBottomColor:scoreColor(d.score)}:{}}>
                    {d.icon} {d.label}
                    <span className="tab-score" style={{color:scoreColor(d.score)}}>{d.score}</span>
                  </button>
                ))}
              </div>

              <div className="report-section">
                {tab === 'services' ? (
                  <ServicesCoverageTab siteUrl={report.url} />
                ) : (
                  <>
                    <div className="tab-summary">
                      <span>{tabData[tab].checks.filter(c=>c.passed).length} passed</span>
                      <span className="tab-fail">{tabData[tab].checks.filter(c=>!c.passed).length} failed</span>
                    </div>
                    <div className="audit-list">
                      {[...tabData[tab].checks].sort((a,b)=>a.passed===b.passed?0:a.passed?1:-1).map(c=>(
                        <AuditItem key={c.id} {...c}/>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* CTA */}
              <div className="audit-cta-section">
                <h3>Need help fixing these issues?</h3>
                <p>Rajesh R specializes in AI & SEO optimization for Kerala businesses. Let's fix your site together.</p>
                <div className="audit-cta-btns">
                  <a href={`https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20audited%20${encodeURIComponent(report.url)}%20using%20your%20free%20tool%20and%20got%20a%20score%20of%20${overall}%2F100.%20Can%20you%20help%20me%20improve%20it?`}
                    target="_blank" rel="noreferrer noopener" className="audit-cta-btn primary">WhatsApp Rajesh</a>
                  <a href="/contact" className="audit-cta-btn secondary">Book Free Consultation</a>
                </div>
              </div>

            </div>
          </section>
        )}

        {/* HOW IT WORKS */}
        {!report && !loading && (
          <section className="audit-how">
            <div className="container">
              <h2 className="audit-how-title">What We Analyze</h2>
              <div className="audit-how-grid">
                {[
                  {icon:<Search size={26}/>, title:'SEO',           color:'#8b5cf6', points:['Title & meta description','H1 / heading structure','Canonical URL','Open Graph tags','JSON-LD structured data','Image alt attributes']},
                  {icon:<Zap size={26}/>,    title:'Performance',   color:'#00d4ff', points:['HTTPS & page size','Render-blocking scripts','Lazy loading images','Script defer / async','Preconnect hints','Inline event handlers']},
                  {icon:<Eye size={26}/>,    title:'Accessibility', color:'#10b981', points:['HTML lang attribute','Form input labels','Main landmark element','Image alt text','Descriptive link text','Safe external links']},
                  {icon:<Sparkles size={26}/>,title:'Gemini AI',    color:'#f59e0b', points:['AI readability score','AEO score (ChatGPT / Gemini)','Content gap analysis','Keyword opportunities','SEO quick wins','AEO recommendations']},
                ].map(c=>(
                  <div key={c.title} className="audit-how-card">
                    <div className="audit-how-icon" style={{color:c.color,background:`${c.color}18`}}>{c.icon}</div>
                    <h3 style={{color:c.color}}>{c.title}</h3>
                    <ul>{c.points.map(p=><li key={p}><CheckCircle size={11}/>{p}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      </div>
    </>
  );
}
